/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ReferenceDot,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import { AutoGraphRounded, BarChartRounded, DataUsageRounded } from "@mui/icons-material";
import { Chip } from "../../Chip";
import { DateRangePickerCalendar } from "../../DateRangePicker";
import { Title } from "../../Title";
import { capitalize } from "../../../utils/utils";
import "../../../routes/pages/painel.css";

interface BasicRangeDate {
  startDate?: string;
  endDate?: string;
}

interface RangeWithGroup extends BasicRangeDate {
  groupBy?: "month" | "year";
}

const NoDataGraph: React.FC<{label: string, icon: React.ReactNode}> = (props) => {
  return (
    <div className="noDataGraphContainer">
      <span className="noDataGraphText">{props.label}</span>
      {props.icon}
    </div>
  );
}

const Graph: React.FC<{
  id?: string
  children: React.ReactNode;
  onChange: (startDate: Date, endDate: Date) => void;
  title?: string;
  style?: React.CSSProperties;
}> = ({ id, children, title, style, onChange }) => {
  const [openAdvancedRange, setOpenAdvancedRange] = React.useState(false);
  const settingsRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="graphContainer" id={id} style={style}>
      {title && (
        <Chip class="graphChip">
          <span className="graphChipTitle">{title}</span>
        </Chip>
      )}
      {children}
      <div className="graphSettingsContainer" ref={settingsRef}>
        <FontAwesomeIcon
          icon={faGear}
          className="graphSettingsIcon"
          onClick={() => {
            setOpenAdvancedRange(!openAdvancedRange);
            if (openAdvancedRange) {
              if (!settingsRef.current) return;
              settingsRef.current.style.overflow = "hidden";
              settingsRef.current.style.width = "18px";
            } else {
              if (!settingsRef.current) return;
              settingsRef.current.style.width = "100%";
              setTimeout(() => {
                if (!settingsRef.current) return;
                settingsRef.current.style.overflow = "visible";
              }, 400);
            }
          }}
        />
        <div className="inputAdvancedRangeContainer">
          <DateRangePickerCalendar onChange={onChange} />
        </div>
      </div>
    </div>
  );
};

// Tutorial mock — mirrors the live painel layout (no Mês/Ano toggle since
// the real UI dropped it; period selection moved fully to the gear menu).
const GraphWithGroup: React.FC<{
  id: string;
  children: React.ReactNode;
  title?: string;
  style?: React.CSSProperties;
}> = ({ id, children, title, style }) => {
  return (
    <div className="graphContainer" id={id} style={style}>
      {title && (
        <Chip class="graphChip">
          <span className="graphChipTitle">{title}</span>
        </Chip>
      )}
      {children}
    </div>
  );
};

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  label?: string;
  format?: (value: any) => string;
}> = ({ active, payload, label, format }) => {
  if (active && payload && payload.length) {
    const headerLabel = label || (payload.length === 1 ? payload[0]?.name : "");
    const showSeriesName = payload.length > 1;
    return (
      <div className="customTooltipGraph" style={payload.length > 1 ? { flexDirection: "column",  borderRadius: "var(--border-radius-md)"} : {}}>
        {headerLabel && (
          <span className="customTooltipGraphLabel">{capitalize(headerLabel)}:</span>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={`tooltip-${index}`} className="customTooltipGraphItem">
            {showSeriesName && (
              <>
                <span
                  className="customTooltipGraphDot"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="customTooltipGraphName">{capitalize(entry.name || "")}:</span>
              </>
            )}
            <span className="customTooltipGraphValue">
              {format ? format(entry.value) : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload }: any) => {
  return (
    <div className="graphLegendContainer">
      {payload.map((entry: any) => {
        const isLine = entry.type === "line";
        return (
          <div key={`${entry.dataKey}-${entry.type}`} className="graphLegendItem">
            {isLine ? (
              <svg
                className="graphLegendGlyph"
                width={22}
                height={12}
                viewBox="0 0 22 12"
                aria-hidden
              >
                <line
                  x1={0}
                  y1={6}
                  x2={22}
                  y2={6}
                  stroke={entry.color}
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <circle cx={11} cy={6} r={3.5} fill={entry.color} />
              </svg>
            ) : (
              <svg
                className="graphLegendGlyph"
                width={12}
                height={12}
                viewBox="0 0 12 12"
                aria-hidden
              >
                <rect
                  x={0}
                  y={0}
                  width={12}
                  height={12}
                  rx={3}
                  ry={3}
                  fill={entry.color}
                />
              </svg>
            )}
            {entry.value}
          </div>
        );
      })}
    </div>
  );
};

interface GraphCostOverviewData {
  period: string;
  totalCost: number;
}

function defaultInitialDateByGroup(groupBy: string): {
  startDate: Date;
} {
  const now = new Date();

  if (groupBy === "month") {
    return {
      startDate: new Date(now.getFullYear(), now.getMonth() - 11, 1),
    };
  }

  return {
    startDate: new Date(now.getFullYear() - 11, 0, 1),
  };
}

const MONTH_FULL_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function lastDayOfMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

// Same helper used by the live painel — kept inline here to avoid a shared
// module just for this. Tutorial mocks default to the "Últimos 12 meses"
// preset so that's what users see when the onboarding renders.
function formatRangeTitle(start: Date, end: Date): string {
  if (!(start instanceof Date) || !(end instanceof Date) || isNaN(start.getTime()) || isNaN(end.getTime())) {
    return "";
  }
  const sd = start.getDate(), sm = start.getMonth(), sy = start.getFullYear();
  const ed = end.getDate(), em = end.getMonth(), ey = end.getFullYear();
  const pad = (n: number) => String(n).padStart(2, "0");

  const today = new Date();
  const ty = today.getFullYear(), tm = today.getMonth(), td = today.getDate();
  const isSameYMD = (d: Date, y: number, m: number, day: number) =>
    d.getFullYear() === y && d.getMonth() === m && d.getDate() === day;

  if (isSameYMD(start, ty, tm, td) && isSameYMD(end, ty, tm, td)) return "Hoje";
  const yest = new Date(ty, tm, td - 1);
  if (
    isSameYMD(start, yest.getFullYear(), yest.getMonth(), yest.getDate()) &&
    isSameYMD(end, yest.getFullYear(), yest.getMonth(), yest.getDate())
  ) return "Ontem";
  const weekStart = new Date(ty, tm, td - today.getDay());
  if (
    isSameYMD(start, weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()) &&
    isSameYMD(end, ty, tm, td)
  ) return "Esta Semana";
  const last7Start = new Date(ty, tm, td - 6);
  if (
    isSameYMD(start, last7Start.getFullYear(), last7Start.getMonth(), last7Start.getDate()) &&
    isSameYMD(end, ty, tm, td)
  ) return "Últimos 7 dias";
  if (sd === 1 && sy === ty && sm === tm && isSameYMD(end, ty, tm, td)) return "Mês Atual";
  const last30Start = new Date(ty, tm, td - 29);
  if (
    isSameYMD(start, last30Start.getFullYear(), last30Start.getMonth(), last30Start.getDate()) &&
    isSameYMD(end, ty, tm, td)
  ) return "Últimos 30 dias";
  const last12Start = new Date(ty, tm - 11, 1);
  if (
    isSameYMD(start, last12Start.getFullYear(), last12Start.getMonth(), last12Start.getDate()) &&
    isSameYMD(end, ty, tm, td)
  ) return "Últimos 12 meses";
  if (sd === 1 && sm === 0 && sy === ty && isSameYMD(end, ty, tm, td)) return "Ano Atual";
  if (sm === 0 && sd === 1 && em === 11 && ed === 31 && sy === ey) return String(sy);
  if (sd === 1 && ed === lastDayOfMonth(end) && sm === em && sy === ey) return `${MONTH_FULL_PT[sm]} de ${sy}`;
  if (sy === ey && sm === em && sd === ed) return `${pad(sd)}/${pad(sm + 1)}/${sy}`;
  if (sy === ey && sm === em) return `${pad(sd)} a ${pad(ed)}/${pad(sm + 1)}/${sy}`;
  if (sy === ey) return `${pad(sd)}/${pad(sm + 1)} a ${pad(ed)}/${pad(em + 1)}/${sy}`;
  return `${pad(sd)}/${pad(sm + 1)}/${sy} a ${pad(ed)}/${pad(em + 1)}/${ey}`;
}

const ACCESS_VIEW_COLORS = {
  viewHall: "var(--accent-primary-6)",
  viewRadio: "var(--accent-secondary-6)",
  viewTV: "var(--accent-tertiary-6)",
  accessHall: "var(--accent-primary-8)",
  accessRadio: "var(--accent-secondary-8)",
  accessTV: "var(--accent-tertiary-8)",
};

const GraphCostOverview: React.FC<RangeWithGroup> = ({
  startDate,
  endDate,
  groupBy,
}) => {
  const [loading, setLoading] = React.useState(true);
  // Tutorial mock: range/group are inert (no UI to change them after the
  // toggle/picker were stripped). useMemo keeps the Date references stable
  // across renders — without it, the data effect's deps change every render
  // and the chart re-fetches in a loop, flickering the skeleton.
  const groupByState: "month" | "year" = groupBy || "month";
  const startDateState = React.useMemo(
    () =>
      startDate
        ? new Date(startDate)
        : defaultInitialDateByGroup(groupByState).startDate,
    [startDate, groupByState],
  );
  const endDateState = React.useMemo(
    () => (endDate ? new Date(endDate) : new Date()),
    [endDate],
  );
  const [data, setData] = React.useState<GraphCostOverviewData[]>([]);

  React.useEffect(() => {
    setLoading(true);
    setTimeout(() => {
        const fetchedDataMonth: GraphCostOverviewData[] = [
            { period: "Jan", totalCost: 120.5 },
            { period: "Feb", totalCost: 98.75 },
            { period: "Mar", totalCost: 150.0 },
            { period: "Abr", totalCost: 200.25 },
            { period: "Mai", totalCost: 175.0 },
            { period: "Jun", totalCost: 220.0 },
            { period: "Jul", totalCost: 190.5 },
            { period: "Ago", totalCost: 210.0 },
            { period: "Set", totalCost: 230.75 },
            { period: "Out", totalCost: 250.0 },
            { period: "Nov", totalCost: 270.0 },
            { period: "Dez", totalCost: 300.0 },
        ];

        const fetchedDataYear: GraphCostOverviewData[] = [
            { period: "2019", totalCost: 1500.0 },
            { period: "2020", totalCost: 1800.5 },
            { period: "2021", totalCost: 2000.75 },
            { period: "2022", totalCost: 2200.0 },
            { period: "2023", totalCost: 2500.25 },
        ];

        if (groupByState === "month") {
            setData(fetchedDataMonth);
        } else {
            setData(fetchedDataYear);
        }
        setLoading(false);
    }, 1000);
  }, [startDateState, endDateState, groupByState]);

  const avgCost = React.useMemo(() => {
    if (!data.length) return 0;
    const sum = data.reduce((acc, d) => acc + (Number.isFinite(d.totalCost) ? d.totalCost : 0), 0);
    return sum / data.length;
  }, [data]);
  const maxCost = React.useMemo(() => {
    if (!data.length) return 0;
    return data.reduce((m, d) => (d.totalCost > m ? d.totalCost : m), -Infinity);
  }, [data]);
  const formatCurrency = (value: number) =>
    `R$ ${value.toFixed(2).replace(".", ",")}`;

  return (
    <GraphWithGroup
      title={`Visão Geral de Custos - ${formatRangeTitle(startDateState, endDateState)}`}
      id="graphCostOverview"
      style={{ minWidth: "400px"}}
    >
      {loading ? (
        <Skeleton
          variant="rounded"
          sx={{ minHeight: "280px", bgcolor: "#99999925" }}
          animation="wave"
        />
      ) : data.length === 0 ?
      <NoDataGraph
        label="Sem custos no período"
        icon={<BarChartRounded className="noDataGraphIcon" />}
      /> :
      (
        <AreaChart
          className="barChartContainer"
          style={{ width: "100%", height: "calc(100% - 30px)" }}
          data={data}
          margin={{ top: 16, right: 16, bottom: 0, left: 0 }}
        >
          <defs>
            <linearGradient id="tutorialCostAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--brand-9)" stopOpacity={0.5} />
              <stop offset="100%" stopColor="var(--brand-5)" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 6"
            stroke="var(--brand-6)"
            strokeOpacity={0.4}
            horizontal
            vertical
            verticalCoordinatesGenerator={({ xAxis }: any) =>
              ((xAxis?.ticks as any[]) ?? [])
                .map((t) => t?.coordinate ?? t?.tickCoord)
                .filter((c): c is number => typeof c === "number")
            }
          />
          <XAxis
            dataKey="period"
            stroke="var(--brand-12)"
            tickFormatter={(value => capitalize(value))}
            tickLine={false}
            axisLine={{ stroke: "var(--brand-6)" }}
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            interval="preserveStartEnd"
            minTickGap={24}
            tickMargin={10}
            height={36}
          />
          <YAxis
            stroke="var(--brand-12)"
            tickFormatter={formatCurrency}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            width={80}
          />
          <Tooltip
            cursor={{ stroke: "var(--brand-9)", strokeDasharray: "3 4" }}
            content={<CustomTooltip format={formatCurrency} />}
          />
          {avgCost > 0 && (
            <ReferenceLine
              y={avgCost}
              stroke="var(--brand-11)"
              strokeOpacity={0.6}
              strokeDasharray="4 4"
              ifOverflow="extendDomain"
              label={{
                value: `Média ${formatCurrency(avgCost)}`,
                position: "right",
                fill: "var(--brand-11)",
                fontSize: 11,
                offset: 6,
              }}
            />
          )}
          <Area
            type="monotone"
            dataKey="totalCost"
            stroke="var(--brand-9)"
            strokeWidth={2.5}
            fill="url(#tutorialCostAreaGradient)"
            dot={{ r: 3, fill: "var(--brand-9)", stroke: "var(--brand-2)", strokeWidth: 1 }}
            activeDot={{ r: 6, fill: "var(--brand-9)", stroke: "var(--brand-2)", strokeWidth: 2 }}
            isAnimationActive
            animationDuration={650}
          />
          {maxCost > 0 && (() => {
            const maxEntry = data.find((d) => d.totalCost === maxCost);
            if (!maxEntry) return null;
            return (
              <ReferenceDot
                x={maxEntry.period}
                y={maxCost}
                r={6}
                fill="var(--accent-primary-9)"
                stroke="var(--brand-2)"
                strokeWidth={2}
                ifOverflow="extendDomain"
              />
            );
          })()}
        </AreaChart>
      )}
    </GraphWithGroup>
  );
};

const GraphCostBreakdown: React.FC<BasicRangeDate> = ({
  startDate,
  endDate,
}) => {
  const now = new Date();
  const [isDefaultRange, setIsDefaultRange] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [startDateState, setStartDateState] = React.useState<Date>(
    startDate
      ? new Date(startDate)
      : new Date(now.getFullYear(), now.getMonth(), 1),
  );
  const [endDateState, setEndDateState] = React.useState<Date>(
    endDate ? new Date(endDate) : now
  );
  const [itemConst, setItemConst] = React.useState<
    { name: string; value: number }[]
  >([]);
  const [totalCost, setTotalCost] = React.useState<number>(0);

  function checkIfIsDefaultRange(sd: Date, ed: Date) {
    const nowWithoutTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const newStartDate = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate(), 0, 0, 0, 0);
    const newEndDate = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate(), 0, 0, 0, 0);

    return (
      newStartDate.getTime() === new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).getTime() &&
      newEndDate.getTime() === nowWithoutTime.getTime()
    );
  }

  React.useEffect(() => {
    setLoading(true);
    // fetch data mock
    setTimeout(() => {
        // These names are rendered verbatim in the legend and tooltip (the Pie
        // below only upper-cases the first letter), so they are user-visible
        // copy, not data keys — "halls" is not a word this product uses.
        const fetchedData: { name: string; value: number }[] = isDefaultRange
        ? [
            { name: "Átrio", value: 350.75 },
            { name: "radios", value: 220.5 },
            { name: "tvs", value: 430.25 },
        ]
        : [
            { name: "Átrio", value: 150.0 },
            { name: "radios", value: 80.0 },
            { name: "tvs", value: 120.0 },
        ];

        setItemConst(fetchedData);
        const total = fetchedData.reduce((acc, item) => acc + item.value, 0);
        setTotalCost(total);
        setLoading(false);
    }, 1000);
  }, [startDateState, endDateState]);

  const COLORS = [
    "var(--accent-primary-9)",
    "var(--accent-secondary-9)",
    "var(--accent-tertiary-9)",
    "var(--brand-9)",
  ];
  const COLORS_SOFT = [
    "var(--accent-primary-7)",
    "var(--accent-secondary-7)",
    "var(--accent-tertiary-7)",
    "var(--brand-7)",
  ];

  const formatCurrency = (value: number) =>
    `R$ ${value.toFixed(2).replace(".", ",")}`;

  const formatCurrencyCompact = (value: number) => {
    const abs = Math.abs(value);
    const sign = value < 0 ? "-" : "";
    const fmt = (v: number) => v.toFixed(v < 10 ? 1 : 0).replace(".", ",").replace(/,0+$/, "");
    if (abs >= 1_000_000_000) return `${sign}R$ ${fmt(abs / 1_000_000_000)} Bi`;
    if (abs >= 1_000_000) return `${sign}R$ ${fmt(abs / 1_000_000)} Mi`;
    if (abs >= 10_000) return `${sign}R$ ${fmt(abs / 1_000)} mil`;
    return formatCurrency(value);
  };

  return (
    <Graph
      title={`Detalhamento de Custos - ${formatRangeTitle(startDateState, endDateState)}`}
      id="graphCostBreakdown"
      onChange={(sd, ed) => {
        setStartDateState(sd);
        setEndDateState(ed);
        setIsDefaultRange(checkIfIsDefaultRange(sd, ed));
      }}
      style={{ minWidth: "300px", maxWidth: "300px"}}
    >
      {loading ? (
        <Skeleton
          variant="rounded"
          sx={{ minHeight: "280px", bgcolor: "#99999925" }}
          animation="wave"
        />
      ) : totalCost === 0 ?
      <NoDataGraph
        label="Sem custos no período"
        icon={<DataUsageRounded className="noDataGraphIcon" />}
      /> :
      (
        <PieChart
          className="pieChartContainer"
          style={{
            width: "100%",
            height: "calc(100% - 10px)",
            aspectRatio: 1,
          }}
          responsive
        >
          <defs>
            {itemConst.map((_, idx) => (
              <radialGradient
                key={`tutorialPieGrad-${idx}`}
                id={`tutorialPieGrad-${idx}`}
                cx="50%"
                cy="50%"
                r="50%"
              >
                <stop offset="50%" stopColor={COLORS_SOFT[idx % COLORS_SOFT.length]} stopOpacity={0.85} />
                <stop offset="100%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={1} />
              </radialGradient>
            ))}
          </defs>
          <Pie
            data={itemConst.map(({ name, value }) => ({
              name: name.charAt(0).toUpperCase() + name.slice(1),
              value,
            }))}
            cx="50%"
            cy="50%"
            innerRadius="68%"
            outerRadius="96%"
            paddingAngle={2}
            cornerRadius={2}
            stroke="none"
            isAnimationActive
            animationDuration={650}
            shapeRendering="geometricPrecision"
            labelLine={false}
            label={false}
          >
            {itemConst.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={`url(#tutorialPieGrad-${index})`}
                stroke="none"
              />
            ))}
            <Label
              position="center"
              dy={-10}
              value="TOTAL"
              fill="var(--brand-11)"
              style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.8px" }}
            />
            <Label
              position="center"
              dy={10}
              value={formatCurrencyCompact(totalCost)}
              fill="var(--brand-12)"
              style={{ fontSize: 18, fontWeight: 600 }}
            />
          </Pie>
          <Tooltip content={<CustomTooltip format={formatCurrency} />} />
          <Legend content={<CustomLegend />} />
        </PieChart>
      )}
    </Graph>
  );
};

interface GraphAccessAndViewByFeatureData {
  period: string;
  viewHall: number;
  viewRadio: number;
  viewTv: number;
  accessHall: number;
  accessRadio: number;
  accessTv: number;
}

// Shared mock data for both Views and Clicks panels — same shape as the
// production /dashboard/by-feature payload so both renderers behave the
// same as their live counterparts.
function useTutorialAccessAndViewData(
  startDate: Date,
  endDate: Date,
  groupBy: "month" | "year",
) {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<GraphAccessAndViewByFeatureData[]>([]);

  React.useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      const monthly: GraphAccessAndViewByFeatureData[] = [
        { period: "Jan", viewHall: 1800, viewRadio: 1200, viewTv: 2200, accessHall: 60, accessRadio: 35, accessTv: 80 },
        { period: "Fev", viewHall: 1600, viewRadio: 1100, viewTv: 2050, accessHall: 55, accessRadio: 32, accessTv: 70 },
        { period: "Mar", viewHall: 2100, viewRadio: 1450, viewTv: 2400, accessHall: 75, accessRadio: 45, accessTv: 90 },
        { period: "Abr", viewHall: 2400, viewRadio: 1700, viewTv: 2700, accessHall: 90, accessRadio: 55, accessTv: 110 },
        { period: "Mai", viewHall: 2200, viewRadio: 1600, viewTv: 2550, accessHall: 80, accessRadio: 50, accessTv: 95 },
        { period: "Jun", viewHall: 2600, viewRadio: 1800, viewTv: 2900, accessHall: 100, accessRadio: 60, accessTv: 120 },
        { period: "Jul", viewHall: 2500, viewRadio: 1750, viewTv: 2850, accessHall: 95, accessRadio: 58, accessTv: 115 },
        { period: "Ago", viewHall: 2700, viewRadio: 1900, viewTv: 3050, accessHall: 105, accessRadio: 65, accessTv: 125 },
        { period: "Set", viewHall: 2900, viewRadio: 2050, viewTv: 3200, accessHall: 115, accessRadio: 72, accessTv: 135 },
        { period: "Out", viewHall: 3100, viewRadio: 2200, viewTv: 3400, accessHall: 125, accessRadio: 78, accessTv: 145 },
        { period: "Nov", viewHall: 3300, viewRadio: 2350, viewTv: 3600, accessHall: 135, accessRadio: 84, accessTv: 155 },
        { period: "Dez", viewHall: 3500, viewRadio: 2500, viewTv: 3800, accessHall: 145, accessRadio: 90, accessTv: 165 },
      ];
      const yearly: GraphAccessAndViewByFeatureData[] = [
        { period: "2019", viewHall: 18000, viewRadio: 12000, viewTv: 22000, accessHall: 600, accessRadio: 350, accessTv: 800 },
        { period: "2020", viewHall: 21000, viewRadio: 14500, viewTv: 25500, accessHall: 720, accessRadio: 420, accessTv: 920 },
        { period: "2021", viewHall: 25000, viewRadio: 17800, viewTv: 28800, accessHall: 850, accessRadio: 510, accessTv: 1080 },
        { period: "2022", viewHall: 29000, viewRadio: 20500, viewTv: 32400, accessHall: 980, accessRadio: 600, accessTv: 1240 },
        { period: "2023", viewHall: 33000, viewRadio: 23500, viewTv: 36800, accessHall: 1130, accessRadio: 690, accessTv: 1410 },
      ];
      setData(groupBy === "month" ? monthly : yearly);
      setLoading(false);
    }, 1000);
    return () => clearTimeout(t);
  }, [startDate, endDate, groupBy]);

  return { data, loading };
}

const GraphViewsByFeature: React.FC<RangeWithGroup> = ({
  startDate,
  endDate,
  groupBy,
}) => {
  const groupByState: "month" | "year" = groupBy || "month";
  const startDateState = React.useMemo(
    () =>
      startDate
        ? new Date(startDate)
        : defaultInitialDateByGroup(groupByState).startDate,
    [startDate, groupByState],
  );
  const endDateState = React.useMemo(
    () => (endDate ? new Date(endDate) : new Date()),
    [endDate],
  );
  const { data, loading } = useTutorialAccessAndViewData(startDateState, endDateState, groupByState);

  return (
    <GraphWithGroup
      title={`Visualizações - ${formatRangeTitle(startDateState, endDateState)}`}
      id="graphViewsByFeature"
      style={{ minWidth: "500px", flexGrow: 1 }}
    >
      {loading ? (
        <Skeleton
          variant="rounded"
          sx={{ minHeight: "250px", bgcolor: "#99999925" }}
          animation="wave"
        />
      ) : data.length === 0 ? (
        <NoDataGraph
          label="Sem visualizações no período"
          icon={<AutoGraphRounded className="noDataGraphIcon" />}
        />
      ) : (
        <AreaChart
          className="barChartContainer"
          style={{ width: "100%", height: "calc(100% - 30px)" }}
          data={data}
          margin={{ top: 16, right: 16, bottom: 0, left: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 6"
            stroke="var(--brand-6)"
            strokeOpacity={0.4}
            horizontal
            vertical
            verticalCoordinatesGenerator={({ xAxis }: any) =>
              ((xAxis?.ticks as any[]) ?? [])
                .map((t) => t?.coordinate ?? t?.tickCoord)
                .filter((c): c is number => typeof c === "number")
            }
          />
          <XAxis
            dataKey="period"
            stroke="var(--brand-12)"
            tickFormatter={(value => capitalize(value))}
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            tickLine={false}
            axisLine={{ stroke: "var(--brand-6)" }}
            interval="preserveStartEnd"
            minTickGap={24}
            tickMargin={10}
            height={36}
          />
          <YAxis
            stroke="var(--brand-12)"
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip
            cursor={{ stroke: "var(--brand-9)", strokeDasharray: "3 4" }}
            content={<CustomTooltip />}
          />
          <Legend content={<CustomLegend />} />
          <Area
            stackId="views"
            type="monotone"
            dataKey="viewHall"
            name="Átrio"
            stroke={ACCESS_VIEW_COLORS.viewHall}
            fill={ACCESS_VIEW_COLORS.viewHall}
            fillOpacity={0.45}
            strokeWidth={1.5}
            legendType="rect"
            isAnimationActive
            animationDuration={650}
          />
          <Area
            stackId="views"
            type="monotone"
            dataKey="viewRadio"
            name="Rádio"
            stroke={ACCESS_VIEW_COLORS.viewRadio}
            fill={ACCESS_VIEW_COLORS.viewRadio}
            fillOpacity={0.45}
            strokeWidth={1.5}
            legendType="rect"
            isAnimationActive
            animationDuration={650}
          />
          <Area
            stackId="views"
            type="monotone"
            dataKey="viewTv"
            name="TV"
            stroke={ACCESS_VIEW_COLORS.viewTV}
            fill={ACCESS_VIEW_COLORS.viewTV}
            fillOpacity={0.45}
            strokeWidth={1.5}
            legendType="rect"
            isAnimationActive
            animationDuration={650}
          />
        </AreaChart>
      )}
    </GraphWithGroup>
  );
};

const GraphClicksByFeature: React.FC<RangeWithGroup> = ({
  startDate,
  endDate,
  groupBy,
}) => {
  const groupByState: "month" | "year" = groupBy || "month";
  const startDateState = React.useMemo(
    () =>
      startDate
        ? new Date(startDate)
        : defaultInitialDateByGroup(groupByState).startDate,
    [startDate, groupByState],
  );
  const endDateState = React.useMemo(
    () => (endDate ? new Date(endDate) : new Date()),
    [endDate],
  );
  const { data, loading } = useTutorialAccessAndViewData(startDateState, endDateState, groupByState);

  return (
    <GraphWithGroup
      title={`Cliques - ${formatRangeTitle(startDateState, endDateState)}`}
      id="graphClicksByFeature"
      style={{ minWidth: "500px", flexGrow: 1 }}
    >
      {loading ? (
        <Skeleton
          variant="rounded"
          sx={{ minHeight: "250px", bgcolor: "#99999925" }}
          animation="wave"
        />
      ) : data.length === 0 ? (
        <NoDataGraph
          label="Sem cliques no período"
          icon={<AutoGraphRounded className="noDataGraphIcon" />}
        />
      ) : (
        <BarChart
          className="barChartContainer"
          style={{ width: "100%", height: "calc(100% - 30px)" }}
          data={data}
          margin={{ top: 16, right: 16, bottom: 0, left: 0 }}
          barCategoryGap="35%"
        >
          <defs>
            <linearGradient id="tutorialClicksHallGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCESS_VIEW_COLORS.accessHall} stopOpacity={0.95} />
              <stop offset="100%" stopColor={ACCESS_VIEW_COLORS.accessHall} stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="tutorialClicksRadioGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCESS_VIEW_COLORS.accessRadio} stopOpacity={0.95} />
              <stop offset="100%" stopColor={ACCESS_VIEW_COLORS.accessRadio} stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="tutorialClicksTvGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={ACCESS_VIEW_COLORS.accessTV} stopOpacity={0.95} />
              <stop offset="100%" stopColor={ACCESS_VIEW_COLORS.accessTV} stopOpacity={0.55} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 6"
            stroke="var(--brand-6)"
            strokeOpacity={0.4}
            horizontal
            vertical={false}
          />
          <XAxis
            dataKey="period"
            stroke="var(--brand-12)"
            tickFormatter={(value => capitalize(value))}
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            tickLine={false}
            axisLine={{ stroke: "var(--brand-6)" }}
            interval="preserveStartEnd"
            minTickGap={24}
            tickMargin={10}
            height={36}
          />
          <YAxis
            stroke="var(--brand-12)"
            tick={{ fontSize: 12, fill: "var(--brand-12)" }}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip
            cursor={{ fill: "var(--brand-3)" }}
            content={<CustomTooltip />}
          />
          <Legend content={<CustomLegend />} />
          <Bar
            stackId="clicks"
            dataKey="accessHall"
            name="Átrio"
            fill="url(#tutorialClicksHallGradient)"
            isAnimationActive
            animationDuration={650}
          />
          <Bar
            stackId="clicks"
            dataKey="accessRadio"
            name="Rádio"
            fill="url(#tutorialClicksRadioGradient)"
            isAnimationActive
            animationDuration={650}
          />
          <Bar
            stackId="clicks"
            dataKey="accessTv"
            name="TV"
            fill="url(#tutorialClicksTvGradient)"
            radius={[6, 6, 0, 0]}
            isAnimationActive
            animationDuration={650}
          />
        </BarChart>
      )}
    </GraphWithGroup>
  );
};

export const PainelPageAmary: React.FC = () => {
  return (
    <div id="painelPageContainer">
      <Title 
      title='Painel'
      subtitle='Tenha uma visão geral do desempenho das suas campanhas e custos associados.'
      />
      <div className="graphsContainer">
        <GraphCostOverview />
        <GraphCostBreakdown />
      </div>
      <div className="graphsContainer">
        <GraphViewsByFeature />
        <GraphClicksByFeature />
      </div>
    </div>
  );
};
